import { Suspense, useEffect, useState } from "react"
import { ErrorBoundary } from "react-error-boundary"
import { useTranslation } from "react-i18next"
import { useSearchParams } from "react-router"
import { LoadingSpinner } from "@/components/loading-spinner"
import { ScrollWrapper } from "@/components/scroll-wrapper"
import { Banner } from "@/components/ui/banner"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Header } from "@/layouts/header"
import { cn } from "@/lib/utils"
import { BetterDocAnamnesisInfoSchemas } from "@/modules/admin/better-doc/components/better-doc-anamnesis-info-schemas"
import { BetterDocExchangeList } from "@/modules/admin/better-doc/components/better-doc-exchange-list"
import { BetterDocHealthSummary } from "@/modules/admin/better-doc/components/better-doc-health-summary"
import { BetterDocLookup } from "@/modules/admin/better-doc/components/better-doc-lookup"
import { BetterDocRecommendationQuery } from "@/modules/admin/better-doc/components/better-doc-recommendation-query"
import { BetterDocSessionDetailPanel } from "@/modules/admin/better-doc/components/better-doc-session-detail-panel"
import { BetterDocTestCaseSender } from "@/modules/admin/better-doc/components/better-doc-test-case-sender"
import {
  BETTER_DOC_TABS,
  BetterDocTab,
  SESSION_ID_SEARCH_PARAMETER,
} from "@/modules/admin/better-doc/constants/better-doc-constants"
import { findEnumValue } from "@/modules/admin/better-doc/lib/find-enum-value"
import { parseSessionIdParameter } from "@/modules/admin/better-doc/lib/parse-session-id-parameter"
import { useBetterDocHealthSummaryQueryLoader } from "@/modules/admin/better-doc/services/useBetterDocHealthSummaryQuery"
import { SofiaEnvironment } from "@/sofia-environment"

export const BetterDoc = () => {
  const { t } = useTranslation()
  const [searchParams, setSearchParams] = useSearchParams()
  const [selectedTab, setSelectedTab] = useState<BetterDocTab>("sessions")
  const [healthSummaryQueryRef, loadHealthSummaryQuery] = useBetterDocHealthSummaryQueryLoader()

  useEffect(() => {
    loadHealthSummaryQuery({}, { fetchPolicy: "network-only" })
  }, [loadHealthSummaryQuery])

  const isTestCaseSenderAvailable = SofiaEnvironment.CurrentEnvironment !== "production"
  const sessionId = parseSessionIdParameter(searchParams.get(SESSION_ID_SEARCH_PARAMETER))
  const activeTab = sessionId === null ? selectedTab : "sessions"

  const closeSession = () => {
    setSearchParams(
      (currentParams) => {
        const nextParams = new URLSearchParams(currentParams)
        nextParams.delete(SESSION_ID_SEARCH_PARAMETER)
        return nextParams
      },
      { replace: true },
    )
  }

  const handleTabChange = (tabValue: string) => {
    const nextTab = findEnumValue(BETTER_DOC_TABS, tabValue)
    if (nextTab === null) {
      return
    }

    setSelectedTab(nextTab)
    if (sessionId !== null) {
      closeSession()
    }
  }

  const handleSessionClose = () => {
    setSelectedTab("sessions")
    closeSession()
  }

  return (
    <>
      <Header title={t("admin.betterDoc.pageTitle")} />
      <div className="grid-cols-1fr-auto bg-background-subdued grid grid-rows-1" data-testid="better-doc-admin-page">
        <ScrollWrapper className="scrollbar-w-1 hover:scrollbar-thumb-surface-pressed scrollbar-track-transparent grid-cols-1 p-8">
          <div className="grid grid-cols-1 gap-4">
            {healthSummaryQueryRef ? (
              <ErrorBoundary
                resetKeys={[healthSummaryQueryRef]}
                fallback={
                  <Banner type="inline" variant="critical" data-testid="better-doc-admin-health-error">
                    {t("admin.betterDoc.requestFailed")}
                  </Banner>
                }>
                <Suspense fallback={<LoadingSpinner size={24} className="flex justify-center p-8" />}>
                  <BetterDocHealthSummary
                    queryRef={healthSummaryQueryRef}
                    onRefresh={() => loadHealthSummaryQuery({}, { fetchPolicy: "network-only" })}
                  />
                </Suspense>
              </ErrorBoundary>
            ) : null}
            <Tabs value={activeTab} onValueChange={handleTabChange}>
              <TabsList variant="secondary">
                <TabsTrigger value="sessions" variant="secondary" data-testid="better-doc-admin-tab-sessions">
                  {t("admin.betterDoc.tabs.sessions")}
                </TabsTrigger>
                <TabsTrigger
                  value="queryBetterDoc"
                  variant="secondary"
                  data-testid="better-doc-admin-tab-query-better-doc">
                  {t("admin.betterDoc.tabs.queryBetterDoc")}
                </TabsTrigger>
                <TabsTrigger
                  value="anamnesisInfoSchemas"
                  variant="secondary"
                  data-testid="better-doc-admin-tab-anamnesis-info-schemas">
                  {t("admin.betterDoc.tabs.anamnesisInfoSchemas")}
                </TabsTrigger>
                <TabsTrigger value="exchanges" variant="secondary" data-testid="better-doc-admin-tab-exchanges">
                  {t("admin.betterDoc.tabs.exchanges")}
                </TabsTrigger>
                {isTestCaseSenderAvailable ? (
                  <TabsTrigger value="testCases" variant="secondary" data-testid="better-doc-admin-tab-test-cases">
                    {t("admin.betterDoc.tabs.testCases")}
                  </TabsTrigger>
                ) : null}
              </TabsList>
              <TabsContent value="sessions" className="p-0 pt-4">
                <div className="flex flex-col gap-4">
                  <div className={cn(sessionId !== null && "hidden")}>
                    <BetterDocLookup />
                  </div>
                  {sessionId === null ? null : (
                    <BetterDocSessionDetailPanel key={sessionId} sessionId={sessionId} onClose={handleSessionClose} />
                  )}
                </div>
              </TabsContent>
              <TabsContent value="queryBetterDoc" className="p-0 pt-4">
                <BetterDocRecommendationQuery />
              </TabsContent>
              <TabsContent value="anamnesisInfoSchemas" className="p-0 pt-4">
                <BetterDocAnamnesisInfoSchemas />
              </TabsContent>
              <TabsContent value="exchanges" className="p-0 pt-4">
                <BetterDocExchangeList />
              </TabsContent>
              {isTestCaseSenderAvailable ? (
                <TabsContent value="testCases" className="p-0 pt-4">
                  <BetterDocTestCaseSender />
                </TabsContent>
              ) : null}
            </Tabs>
          </div>
        </ScrollWrapper>
      </div>
    </>
  )
}
